import React, { useEffect, useRef, useState } from "react";
import { CaretDownIcon } from "@phosphor-icons/react";

import {
  Wrapper,
  Label,
  HelperText,
  IconStyles,
  SelectWrapper,
  Dropdown,
  Option
} from "components/Input/styled";

import Icon from "components/Icon/Icon";
import AvatarNameBlock from "components/AvatarNameBlock/index";

const Select = ({
  id,
  label,
  helperText,
  options = [],
  placeholder = "Select...",
  value,
  onChange,
  size = "M",
  disabled = false,
  required = false,
  className,
  isMulti = false,
  maxSelect = null,
  selectWrapperClassName
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false); // track typing state
  const selectRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setOpen(false);
        setIsTyping(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update displayed text based on selected items
  useEffect(() => {
    if (!options || options.length === 0) return;

    if (isMulti) {
      const selectedOptions = options.filter((opt) =>
        value?.includes(opt.value)
      );
      const count = selectedOptions.length;

      if (count === 0) {
        setInputValue("");
      } else if (count <= 2) {
        setInputValue(selectedOptions.map((opt) => opt.label).join(", "));
      } else {
        const firstTwo = selectedOptions.slice(0, 2).map((opt) => opt.label);
        setInputValue(`${firstTwo.join(", ")} +${count - 2}`);
      }
    } else {
      // SINGLE SELECT CASE — allow both string or array form
      let selectedLabels = [];

      if (Array.isArray(value)) {
        selectedLabels = options
          .filter(
            (opt) => value.includes(opt.value) || value.includes(opt.label)
          )
          .map((opt) => opt.label);
      } else {
        const selectedOption = options.find(
          (opt) => opt.label === value || opt.value === value
        );
        if (selectedOption) selectedLabels = [selectedOption.label];
      }

      setInputValue(selectedLabels.join(", "));
    }
  }, [value, options, isMulti]);

  const handleSelect = (optionValue, e) => {
    e.stopPropagation();
    if (isMulti) {
      // toggle logic
      const newValues = Array.isArray(value) ? [...value] : [];
      const index = newValues.indexOf(optionValue);
      if (index > -1) {
        // remove if already selected
        newValues.splice(index, 1);
      } else {
        // check max limit
        if (!maxSelect || newValues.length < maxSelect) {
          newValues.push(optionValue);
        } else {
          return; // do nothing if max reached
        }
      }

      onChange?.(newValues);
      return;
    }

    // single select
    const selectedOption = options.find((opt) => opt.value === optionValue);
    setInputValue(selectedOption?.label || "");
    setOpen(false);
    setIsTyping(false);
    onChange?.(optionValue);
  };

  // If typing, filter results; else show all
  const filteredOptions = isTyping
    ? options.filter((opt) =>
        opt.label.toLowerCase().includes(inputValue.toLowerCase())
      )
    : options;

  return (
    <Wrapper size={size} className={className} ref={selectRef}>
      {label && (
        <Label htmlFor={id} size={size}>
          {label}
          {required && <span>*</span>}
        </Label>
      )}

      <SelectWrapper
        size={size}
        disabled={disabled}
        className={`${open ? "open" : ""} ${selectWrapperClassName}`}
      >
        {!isMulti &&
          value &&
          (() => {
            const option = options?.find(
              (o) => o.value === value || o.label === value
            );

            if (!option) return null;

            const hasImage =
              option?.image ||
              option?.profile_image ||
              (option?.label && option?.bgColor);

            if (!hasImage) return null; // don't show section

            return (
              <div className="option-wrapper">
                {option?.image || option?.profile_image ? (
                  <img
                    src={option.profile_image || option.image}
                    alt={option.label || "option"}
                    width={16}
                    height={16}
                    style={{
                      borderRadius: "50%",
                      objectFit: "cover"
                    }}
                  />
                ) : (
                  <AvatarNameBlock
                    name={option.label}
                    bgColor={option.bgColor}
                    size={16}
                    fontSize={6}
                  />
                )}
              </div>
            );
          })()}

        {/* ICON ONLY (non-breaking addition) */}
        {!isMulti &&
          value &&
          (() => {
            const option = options?.find(
              (o) => o.value === value || o.label === value
            );

            if (!option?.icon) return null;

            return (
              <div className="option-wrapper">
                <Icon
                  component={option.icon}
                  format="outline"
                  weight="regular"
                  color={option.iconColor || "#A5A5A5"}
                  size={14}
                />
              </div>
            );
          })()}

        <input
          ref={inputRef}
          id={id}
          value={inputValue || ""}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => {
            const val = e.target.value;
            setInputValue(val);
            setIsTyping(val.trim() !== "");

            if (val.trim() === "") {
              if (isMulti) {
                onChange?.([]); // clear all selections
              } else {
                onChange?.(null); // clear single selection
              }
            }

            if (!open) setOpen(true);
          }}
          onFocus={() => !disabled && setOpen(true)}
          className="select-input-filed"
        />

        <IconStyles
          size={size}
          className="right-icon"
          onClick={() => !disabled && setOpen((prev) => !prev)}
        >
          <div
            style={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "all 0.3s ease-in-out",
              display: "inline-block"
            }}
          >
            <Icon
              component={CaretDownIcon}
              format="outline"
              weight="regular"
              color="#A5A5A5"
            />
          </div>
        </IconStyles>

        {open && (
          <Dropdown size={size}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const hasVisual =
                  option?.image ||
                  option?.profile_image ||
                  (option?.label && option?.bgColor);

                const isOptionDisabled =
                  maxSelect &&
                  !value.includes(option.value) &&
                  value.length >= maxSelect;

                return (
                  <Option
                    key={option.value}
                    size={size}
                    onClick={(e) => handleSelect(option.value, e)}
                    selected={
                      isMulti
                        ? value.includes(option.value)
                        : option.value === value
                    }
                    isMulti={isMulti}
                    className="select-checkbox"
                    style={{
                      cursor: isOptionDisabled ? "not-allowed" : "pointer"
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                    >
                      {isMulti && (
                        <input
                          type="checkbox"
                          checked={value.includes(option.value)}
                          readOnly
                          disabled={isOptionDisabled}
                          className={
                            isOptionDisabled ? "disabled-checkbox" : ""
                          }
                        />
                      )}

                      {/* show only if image / avatar exists */}
                      {hasVisual &&
                        (option?.image || option?.profile_image ? (
                          <img
                            src={option.profile_image || option.image}
                            alt={option.label || "option"}
                            width={16}
                            height={16}
                            style={{
                              borderRadius: "50%",
                              objectFit: "cover"
                            }}
                          />
                        ) : (
                          <AvatarNameBlock
                            name={option.label}
                            bgColor={option.bgColor}
                            size={16}
                            fontSize={6}
                          />
                        ))}

                      {option?.icon && (
                        <Icon
                          component={option.icon}
                          format="outline"
                          weight="regular"
                          color={option.iconColor || "#A5A5A5"}
                          size={14}
                        />
                      )}

                      {option.label}
                    </div>
                  </Option>
                );
              })
            ) : (
              <div
                style={{
                  padding: "8px",
                  textAlign: "center",
                  color: "#999",
                  fontSize: "13px"
                }}
              >
                No options found
              </div>
            )}
          </Dropdown>
        )}
      </SelectWrapper>

      {helperText && <HelperText size={size}>{helperText}</HelperText>}
    </Wrapper>
  );
};

export default Select;
